import { useState } from 'react';
import { Facebook, Instagram, AtSign, Youtube, Music2, Tag, Plus, Trash2, X } from 'lucide-react';
import type { ContentLabel, SocialPlatform, SocialContentItem } from '@unitly/contracts';
import {
  useAssignContentLabels, useContentLabels, useDeleteContentLabel, useSaveContentLabel, useSocialContent,
} from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { PageLoader, EmptyState } from '../../ui/primitives.tsx';
import { Sheet } from '../../ui/Sheet.tsx';
import { useToast } from '../../ui/Toast.tsx';
import { ApiError } from '../../api/client.ts';
import { WorkspaceChannelsPanel } from './WorkspaceChannels.tsx';
import { relativeTime } from '../../lib/dates.ts';

const PLATFORM_ICON: Record<SocialPlatform, typeof Facebook> = {
  facebook: Facebook, instagram: Instagram, threads: AtSign, youtube: Youtube, tiktok: Music2,
};

function LabelChip({ label, onRemove }: { label: ContentLabel; onRemove?: () => void }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999,
      background: `${label.color}22`, color: label.color, fontSize: 12, fontWeight: 600,
    }}>
      {label.name}
      {onRemove && <button type="button" onClick={onRemove} style={{ all: 'unset', cursor: 'pointer', display: 'flex' }}><X size={11} /></button>}
    </span>
  );
}

function ContentCard({ item, labels, canManage }: { item: SocialContentItem; labels: ContentLabel[]; canManage: boolean }) {
  const { orgId } = useOrgCtx();
  const assign = useAssignContentLabels(orgId, item.workspaceId);
  const [picking, setPicking] = useState(false);
  const Icon = PLATFORM_ICON[item.platform];
  const activeIds = item.labels.map((l) => l.id);

  return (
    <div className="card card-pad" style={{ display: 'flex', gap: 12 }}>
      {item.thumbnailUrl ? (
        <img src={item.thumbnailUrl} alt="" style={{ width: 64, height: 64, borderRadius: 10, objectFit: 'cover', flex: 'none' }} />
      ) : (
        <span className="connector-logo" style={{ width: 64, height: 64, flex: 'none' }}><Icon size={26} /></span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <Icon size={13} style={{ opacity: 0.6 }} />
          <span className="hint" style={{ padding: 0 }}>{relativeTime(item.publishedAt)} fa</span>
        </div>
        {item.permalink ? (
          <a href={item.permalink} target="_blank" rel="noreferrer" className="content-caption" title={item.caption ?? undefined} style={{ color: 'inherit', marginBottom: 6, fontSize: 14.5 }}>
            {item.caption || '(senza testo)'}
          </a>
        ) : (
          <div className="content-caption" title={item.caption ?? undefined} style={{ marginBottom: 6, fontSize: 14.5 }}>{item.caption || '(senza testo)'}</div>
        )}
        <div className="hint" style={{ padding: 0, marginBottom: 8, display: 'flex', gap: 10 }}>
          {item.metrics.likes != null && <span>❤ {item.metrics.likes}</span>}
          {item.metrics.comments != null && <span>💬 {item.metrics.comments}</span>}
          {item.metrics.shares != null && <span>↗ {item.metrics.shares}</span>}
          {item.metrics.reach != null && <span>👁 {item.metrics.reach}</span>}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', position: 'relative' }}>
          {item.labels.map((l) => (
            <LabelChip key={l.id} label={l} onRemove={canManage ? () => assign.mutate({ connectionId: item.connectionId, externalId: item.externalId, labelIds: activeIds.filter((id) => id !== l.id) }) : undefined} />
          ))}
          {canManage && labels.length > 0 && (
            <>
              <button type="button" className="chip" style={{ padding: '3px 8px', fontSize: 12 }} onClick={() => setPicking((v) => !v)}>
                <Tag size={12} /> Etichetta
              </button>
              {picking && (
                <div className="card card-pad" style={{ position: 'absolute', top: 26, left: 0, zIndex: 5, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 160 }}>
                  {labels.map((l) => {
                    const active = activeIds.includes(l.id);
                    return (
                      <label key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                        <input type="checkbox" checked={active}
                          onChange={() => assign.mutate({
                            connectionId: item.connectionId, externalId: item.externalId,
                            labelIds: active ? activeIds.filter((id) => id !== l.id) : [...activeIds, l.id],
                          })} />
                        <LabelChip label={l} />
                      </label>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function LabelsManagerSheet({ open, onClose, workspaceId }: { open: boolean; onClose: () => void; workspaceId: string }) {
  const { orgId } = useOrgCtx();
  const labels = useContentLabels(orgId, workspaceId);
  const save = useSaveContentLabel(orgId, workspaceId);
  const del = useDeleteContentLabel(orgId, workspaceId);
  const toast = useToast();
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3B82F6');

  return (
    <Sheet open={open} onClose={onClose} title="Etichette contenuti" variant="center" labelledBy="labels-title">
      <div className="card list" style={{ marginBottom: 16 }}>
        {(labels.data ?? []).map((l) => (
          <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px' }}>
            <LabelChip label={l} />
            <span style={{ flex: 1 }} />
            <button type="button" className="btn btn-plain btn-sm" style={{ color: 'var(--red)' }}
              onClick={() => del.mutate(l.id, { onError: (e) => toast(e instanceof ApiError ? e.message : 'Impossibile eliminare') })}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {labels.data?.length === 0 && <div className="hint" style={{ padding: 14 }}>Nessuna etichetta creata.</div>}
      </div>
      <form onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        save.mutate({ workspaceId, name: name.trim(), color }, {
          onSuccess: () => setName(''),
          onError: (err) => toast(err instanceof ApiError ? err.message : 'Impossibile salvare l’etichetta'),
        });
      }} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ width: 36, height: 36, border: 'none', borderRadius: 8, padding: 0 }} />
        <input className="input" placeholder="Nome etichetta" value={name} onChange={(e) => setName(e.target.value)} maxLength={30} style={{ flex: 1 }} />
        <button type="submit" className="btn btn-primary btn-sm" disabled={save.isPending}><Plus size={15} /> Aggiungi</button>
      </form>
    </Sheet>
  );
}

export function SocialContentView({ workspaceId, canManage }: { workspaceId: string; canManage: boolean }) {
  const { orgId } = useOrgCtx();
  const [platform, setPlatform] = useState<SocialPlatform | 'all'>('all');
  const [labelId, setLabelId] = useState<string | null>(null);
  const [managingLabels, setManagingLabels] = useState(false);
  const labels = useContentLabels(orgId, workspaceId);
  const content = useSocialContent(orgId, workspaceId, platform === 'all' ? undefined : platform, labelId ?? undefined);

  return (
    <>
      <WorkspaceChannelsPanel workspaceId={workspaceId} canManage={canManage} />
      <div className="chips" role="tablist" aria-label="Piattaforma" style={{ marginBottom: 12 }}>
        {([['all', 'Tutte'], ['facebook', 'Facebook'], ['instagram', 'Instagram'], ['threads', 'Threads'], ['youtube', 'YouTube'], ['tiktok', 'TikTok']] as const).map(([v, label]) => (
          <button key={v} type="button" role="tab" className="chip" aria-pressed={platform === v} onClick={() => setPlatform(v)}>{label}</button>
        ))}
      </div>
      <div className="chips" role="tablist" aria-label="Etichetta" style={{ marginBottom: 16 }}>
        <button type="button" className="chip" aria-pressed={labelId === null} onClick={() => setLabelId(null)}>Tutti i contenuti</button>
        {(labels.data ?? []).map((l) => (
          <button key={l.id} type="button" className="chip" aria-pressed={labelId === l.id} onClick={() => setLabelId(l.id === labelId ? null : l.id)}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: l.color, display: 'inline-block', marginRight: 6 }} />
            {l.name}
          </button>
        ))}
        {canManage && (
          <button type="button" className="chip" aria-label="Gestisci etichette" onClick={() => setManagingLabels(true)}><Tag size={13} /></button>
        )}
      </div>
      {(content.data?.unavailable ?? []).map((line) => (
        <div key={line} className="form-error" role="status" style={{ marginBottom: 10 }}>{line}</div>
      ))}
      {content.isLoading ? <PageLoader /> : !content.data?.items.length ? (
        <div className="card"><EmptyState tone="neutral" title="Nessun contenuto">
          {labelId ? 'Nessun contenuto con questa etichetta.' : 'Collega un account per vedere qui i contenuti pubblicati.'}
        </EmptyState></div>
      ) : (
        <div className="content-grid">
          {content.data.items.map((item) => <ContentCard key={item.id} item={item} labels={labels.data ?? []} canManage={canManage} />)}
        </div>
      )}
      <LabelsManagerSheet open={managingLabels} onClose={() => setManagingLabels(false)} workspaceId={workspaceId} />
    </>
  );
}
